<template>
  <div class="phone-number-input">
    <v-text-field
      v-model="national"
      :label="label"
      :variant="variant"
      :density="density"
      :color="color"
      :disabled="disabled"
      :readonly="readonly"
      :rules="allRules"
      :hint="computedHint"
      :persistent-hint="persistentHint"
      :clearable="clearable"
      :hide-details="hideDetails"
      inputmode="tel"
      autocomplete="tel-national"
      @update:model-value="onNationalInput"
      @blur="onBlur"
    >
      <!-- Country picker lives inside the field so the flag + dial code read
           as one control. Selecting a country only prepends its calling code;
           it never rewrites the digits the user already typed. -->
      <template #prepend-inner>
        <v-menu
          v-model="menuOpen"
          :close-on-content-click="false"
          location="bottom start"
          :disabled="disabled || readonly"
        >
          <template #activator="{ props: menuProps }">
            <button
              type="button"
              class="ph-country-btn"
              v-bind="menuProps"
              :disabled="disabled || readonly"
              :aria-label="`Country code ${dialCode}`"
              @click.stop
            >
              <span :class="['fi', 'fi-' + (selectedIso || '').toLowerCase()]" class="ph-flag" />
              <span class="ph-dial">{{ dialCode }}</span>
              <v-icon size="16" class="ph-chevron">mdi-menu-down</v-icon>
            </button>
          </template>

          <v-card min-width="300" max-width="360" class="ph-country-menu">
            <div class="pa-2">
              <v-text-field
                v-model="countrySearch"
                density="compact"
                variant="outlined"
                hide-details
                autofocus
                clearable
                placeholder="Search country..."
                prepend-inner-icon="mdi-magnify"
              />
            </div>
            <v-divider />
            <v-list density="compact" class="ph-country-list">
              <v-list-item
                v-for="c in filteredCountries"
                :key="c.code"
                :active="c.code === selectedIso"
                @click="selectCountry(c.code)"
              >
                <template #prepend>
                  <span :class="['fi', 'fi-' + c.code.toLowerCase()]" class="ph-flag mr-3" />
                </template>
                <v-list-item-title class="text-body-2">{{ c.name }}</v-list-item-title>
                <template #append>
                  <span class="text-caption text-medium-emphasis">{{ c.dialCode }}</span>
                </template>
              </v-list-item>
              <v-list-item v-if="filteredCountries.length === 0">
                <v-list-item-title class="text-caption text-medium-emphasis">
                  No country found
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
    </v-text-field>
  </div>
</template>

<script setup>
/**
 * PhoneNumberInput — a single, reusable phone field for the whole app.
 *
 * WHAT IT DOES
 *  - Shows a country picker (SVG flag + calling code) glued to the input.
 *  - Auto-fills the calling code from the selected country, so the number
 *    that lands in the DB is always full E.164 ("+<cc><national>").
 *  - Default country comes from the company's own setting
 *    (Settings -> Reference Data -> Company Settings -> Region & Locale ->
 *    Country / `country_code`), NOT a new hard-coded value. Falls back to the
 *    explicit `default-country` prop, then to US (+1) so the field always works
 *    even pre-login (registration screen).
 *
 * WHAT IT DOES NOT DO
 *  - It does NOT touch the staff LOGIN flow or backend phone normalization.
 *    v-model stays a plain string, so it is a drop-in replacement for a plain
 *    <v-text-field v-model="phone">. The backend still re-normalizes on save,
 *    so existing records / logins are unaffected.
 */
import { ref, computed, watch, onMounted } from 'vue'
import {
  parsePhoneNumberFromString,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  getCountryCallingCode,
} from 'libphonenumber-js'
import { COUNTRIES, getCountry } from '@/constants/countries'
import { useCompanyContext } from '@/composables/useCompanyContext'

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: 'Phone Number' },
  // Explicit ISO-2 default (e.g. 'US'). When null, the company setting is used.
  defaultCountry: { type: String, default: null },
  // Whether to read the company's country_code as the default (in-app forms).
  // Set to false on pre-login screens (registration) where there is no company
  // context yet — then `defaultCountry` (or US) is used.
  useCompanyDefault: { type: Boolean, default: true },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  density: { type: String, default: 'comfortable' },
  variant: { type: String, default: 'outlined' },
  color: { type: String, default: 'accent' },
  hint: { type: String, default: '' },
  persistentHint: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true },
  hideDetails: { type: [Boolean, String], default: false },
  rules: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'update:valid', 'blur'])

const menuOpen = ref(false)
const countrySearch = ref('')
const selectedIso = ref('US')
const national = ref('')
// Guards the modelValue watcher from reacting to our own emits.
let lastEmitted = null

const { context: companyContext, load: loadCompanyContext } = useCompanyContext()

/** Calling code (with +) for the currently selected country. */
const dialCode = computed(() => {
  const iso = selectedIso.value
  try {
    return '+' + getCountryCallingCode(iso)
  } catch (e) {
    return getCountry(iso)?.dialCode || ''
  }
})

const filteredCountries = computed(() => {
  const q = (countrySearch.value || '').trim().toLowerCase()
  if (!q) return COUNTRIES
  return COUNTRIES.filter((c) => {
    const hay = `${c.name} ${c.code} ${c.dialCode}`.toLowerCase()
    return hay.includes(q)
  })
})

function resolveDefaultIso() {
  const explicit = (props.defaultCountry || '').toUpperCase()
  if (explicit && getCountry(explicit)) return explicit
  if (props.useCompanyDefault) {
    const cc = (companyContext.value?.country_code || '').toUpperCase()
    if (cc && getCountry(cc)) return cc
  }
  return 'US'
}

/** Build the canonical E.164 string from the current country + typed number. */
function computeE164() {
  const raw = (national.value || '').trim()
  if (!raw) return ''
  try {
    const parsed = parsePhoneNumberFromString(raw, selectedIso.value)
    if (parsed && parsed.number) return parsed.number
  } catch (e) { /* fall through to best-effort */ }
  // Best-effort: keep the digits and prepend the selected calling code so we
  // never lose what the user typed while they are mid-entry.
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''
  const cc = (dialCode.value || '').replace('+', '')
  // If the user already typed the country code, don't double it.
  if (cc && digits.startsWith(cc)) return '+' + digits
  return '+' + cc + digits
}

const isValidNow = computed(() => {
  const raw = (national.value || '').trim()
  if (!raw) return !props.required
  const e164 = computeE164()
  try {
    return isValidPhoneNumber(e164) || isPossiblePhoneNumber(e164)
  } catch (e) {
    return false
  }
})

const computedHint = computed(() => {
  if (props.hint) return props.hint
  const raw = (national.value || '').trim()
  if (!raw) return ''
  const e164 = computeE164()
  if (!e164) return ''
  try {
    const parsed = parsePhoneNumberFromString(e164)
    if (parsed) return `Saved as ${parsed.formatInternational()}`
  } catch (e) { /* ignore */ }
  return `Saved as ${e164}`
})

const allRules = computed(() => {
  const base = []
  if (props.required) {
    base.push((v) => (!!(v && String(v).trim())) || 'Phone is required')
  }
  base.push(() => {
    const raw = (national.value || '').trim()
    if (!raw) return true
    const e164 = computeE164()
    try {
      if (isPossiblePhoneNumber(e164)) return true
    } catch (e) { /* ignore */ }
    return 'Enter a valid phone number for the selected country'
  })
  return [...base, ...props.rules]
})

function emitValue() {
  const e164 = computeE164()
  lastEmitted = e164
  emit('update:modelValue', e164)
  emit('update:valid', isValidNow.value)
}

function onNationalInput() {
  emitValue()
}

function onBlur(e) {
  // Tidy the display to the national format once the user leaves the field,
  // without ever changing the underlying E.164 that we already emitted.
  const raw = (national.value || '').trim()
  if (raw) {
    try {
      const parsed = parsePhoneNumberFromString(raw, selectedIso.value)
      if (parsed) national.value = parsed.formatNational()
    } catch (err) { /* keep raw */ }
  }
  emit('blur', e)
}

function selectCountry(iso) {
  selectedIso.value = iso
  menuOpen.value = false
  countrySearch.value = ''
  emitValue()
}

/** Initialise the field from an incoming stored value (E.164 or raw). */
function initFromModel(v) {
  const val = (v == null ? '' : String(v)).trim()
  if (!val) {
    selectedIso.value = resolveDefaultIso()
    national.value = ''
    lastEmitted = ''
    return
  }
  try {
    const parsed = parsePhoneNumberFromString(val)
    if (parsed) {
      selectedIso.value = parsed.country || resolveDefaultIso()
      national.value = parsed.formatNational()
      lastEmitted = parsed.number
      return
    }
  } catch (e) { /* fall through */ }
  // Not parseable as E.164 — show as typed, keep default country.
  selectedIso.value = resolveDefaultIso()
  national.value = val
  lastEmitted = val
}

watch(
  () => props.modelValue,
  (v) => {
    // Ignore the echo of our own emit; only react to real external changes
    // (e.g. a dialog being opened with a prefilled customer phone).
    if (v === lastEmitted) return
    initFromModel(v)
  },
)

onMounted(async () => {
  if (props.useCompanyDefault && !props.defaultCountry) {
    try { await loadCompanyContext() } catch (e) { /* non-blocking */ }
  }
  initFromModel(props.modelValue)
})
</script>

<style scoped>
.ph-country-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 100%;
  padding: 0 8px 0 0;
  margin-right: 6px;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  cursor: pointer;
  background: transparent;
  white-space: nowrap;
}
.ph-country-btn:disabled {
  cursor: default;
  opacity: 0.6;
}
.ph-flag {
  width: 20px;
  height: 15px;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(var(--v-theme-on-surface), 0.06);
  flex-shrink: 0;
}
.ph-dial {
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface));
}
.ph-chevron {
  margin-left: -2px;
  opacity: 0.6;
}
.ph-country-list {
  max-height: 280px;
  overflow-y: auto;
}
</style>
